/**
 * Biomarkers Component
 *
 * This component manages the display, creation, and editing of biomarkers. It includes functionality
 * for handling pagination, deleting biomarkers, and updating the biomarker list.
 *
 * File Name: Biomarkers.tsx
 * Date: 30-07-24
 */

import React, { useEffect, useState } from "react";
import editIcon from "../../assets/images/edit-icon.png";
import deleteIcon from "../../assets/images/delete-icon.png";
import TableWrapper from "../../Components/GlobalComponents/TableWrapper";
import BreadCrumb from "../../Components/GlobalComponents/BreadCrumb";
import UpsertBiomarker from "../../Components/AdminComponents/UpsertBiomarker";
import { IBiomarkerItems, IqueryParams } from "src/types/GlobalInterfaces";
import { IPaginated } from "src/types/paginated";
import ConfirmationModal from "src/Components/GlobalComponents/ConfirmationModal";
import httpRequest from "src/helpers/httpRequest";
import QueryString from "qs";
import { toast } from "react-toastify";
import { truncateString } from "src/helpers/helperFunctions";
import SearchFilterInput from "src/Components/GlobalComponents/SearchFilterInput";
import { useSelector } from "react-redux";
import { RootState } from "@store/store";
import { canWrite } from "src/helpers/helperFunctions";
// Define the response type for biomarker API
interface IBiomarkesResponse {
  documents: IBiomarkerItems[];
  paginated: IPaginated;
}

const Biomarkers: React.FC = () => {
  const { userDetails } = useSelector((store: RootState) => store.appReducer);
  const hasWriteAccess = canWrite(userDetails, "diagnosticTest", false);

  const [isUpsert, setIsUpsert] = useState<boolean>(false); // State to manage the upsert mode (create/edit biomarker)
  const [isEdit, setIsEdit] = useState<string | boolean>(false); // State to determine if the component is in edit mode

  const [deleteLoader, setDeleteLoader] = useState<boolean>(false); // State to manage the loader during biomarker deletion
  const [isLoader, setIsLoader] = useState<boolean>(false); // State to manage the loader for fetching biomarkers
  const [allBiomarkers, setAllBiomarkers] = useState<IBiomarkerItems[]>([]); // State to store the list of biomarkers
  const [paginated, setPaginated] = useState<IPaginated>({
    currentPage: 1,
    totalPages: 1,
  }); // state for handle pagination like a current page, page limit & total pages
  const [deleteModal, setDeleteModal] = useState<boolean>(false); // State to control the visibility of the delete confirmation modal
  const [deleteId, setDeleteId] = useState<string>(""); // State to store the ID of the biomarker to be deleted

  const [queryParams, setQueryParams] = useState<IqueryParams>({
    page: 1,
    limit: 10,
    search: "",
    counter: 1,
  }); // State for query parameters in API requests

  // Function to toggle between create and edit mode
  const handleClose = () => {
    setIsEdit(false);
    setIsUpsert((prev) => !prev);
  };

  // Function to set the component to edit mode and populate it with biomarker data
  const handleEdit = (id: string) => {
    if (!hasWriteAccess) {
      return;
    }
    setIsUpsert(true);
    setIsEdit(id);
  };

  // Function to handle page change in pagination
  const handlePageChange = (e: number) => {
    setQueryParams((prev) => ({
      ...prev,
      page: e,
    }));
  };

  // Function to show the delete confirmation modal and set the ID of the biomarker to be deleted
  const handleDelete = (id: string) => {
    if (!hasWriteAccess) {
      return;
    }
    setDeleteModal(true);
    setDeleteId(id);
  };

  // Function to close the delete confirmation modal
  const deleteModalClose = () => {
    setDeleteModal(false);
    setDeleteId("");
  };

  // Function to update the list of categories after creating or editing a biomarker
  const handleUpdateList = () => {
    setQueryParams((prev) => ({
      ...prev,
      counter: (prev.counter ?? 0) + 1,
    }));
    setIsUpsert(false);
  };

  // Function change search text for api query params
  const handleChangeText = (txt: string) => {
    setQueryParams((prev) => ({
      ...prev,
      page: 1,
      search: txt,
    }));
  };

  // API_CALL: Function to delete a biomarker by its ID
  const deleteBiomarker = async () => {
    setDeleteLoader(true);
    const { res, err } = await httpRequest<any>({
      method: "delete",
      path: `/biomarker/delete/${deleteId}`,
      params: {},
    });
    if (res) {
      toast("Biomarker deleted successfully.", { type: "success" });
      setQueryParams((prev) => ({
        ...prev,
        counter: (prev.counter ?? 0) + 1,
        page:
          allBiomarkers?.length > 1
            ? prev?.page
            : prev?.page > 1
            ? prev?.page - 1
            : prev?.page,
      }));
      setDeleteModal(false);
      setDeleteId("");
    } else {
      toast(err, { type: "error" });
    }
    setDeleteLoader(false);
  };

  // Effect to fetch biomarkers data based on query parameters
  useEffect(() => {
    (async () => {
      setIsLoader(true);
      let { counter, ...remainingParams } = queryParams;
      const { res, err } = await httpRequest<IBiomarkesResponse>({
        path: `/biomarker/all?${QueryString.stringify(remainingParams)}`,
      });
      if (res) {
        setAllBiomarkers(res?.documents);
        setPaginated(res?.paginated);
      } else {
        toast(err, { type: "error" });
      }
      setIsLoader(false);
    })();
  }, [queryParams]);

  return (
    <>
      <ConfirmationModal
        show={deleteModal}
        onClose={deleteModalClose}
        title="Are you sure you want to delete?"
        leftBtnText="No"
        rightBtnText="Yes"
        onConfirm={deleteBiomarker}
        loader={deleteLoader}
      />
      <BreadCrumb
        current="Biomarkers"
        previous="dashboard"
        navigateUrl="/admin"
        btnText={"Add Biomarker"}
        btnShow={true}
        onPress={() => {
          if (!hasWriteAccess) {
            return;
          }
          handleClose();
        }}
        isUpsert={isUpsert}
        btnClassName={`${!hasWriteAccess ? "cursor-not-allowed" : ""}`}
        BtnId="add-biomarker"
      />
      {isUpsert ? (
        <UpsertBiomarker onSuccess={handleUpdateList} isEdit={isEdit} />
      ) : (
        <div className="table-container">
          <SearchFilterInput onChangeText={handleChangeText} />
          <TableWrapper
            isLoading={isLoader}
            thead={[
              "S.No",
              "Biomarker",
              "Measurement Unit",
              "Description",
              "Action",
            ]}
            page={paginated?.currentPage || 1}
            total={paginated?.totalPages || 0}
            columnWidth="6% 19% 25% 40% 10%"
            onChange={handlePageChange}
          >
            <div className="tbody-content-height">
              {allBiomarkers?.map((item: IBiomarkerItems, index: number) => (
                <ul
                  key={index}
                  className="font-size-16px tbody-light-table font-Poppins-Regular grid"
                  style={{ gridTemplateColumns: "6% 19% 25% 40% 10%" }}
                >
                  <li>
                    {(paginated?.currentPage - 1) * queryParams.limit +
                      (index + 1)}
                  </li>
                  <li>{item?.name || ""}</li>
                  <li>{item?.measurementUnit || ""}</li>
                  <li title={item?.shortDescription}>
                    <span className="truncate block">
                      {item?.shortDescription || ""}
                    </span>
                  </li>
                  <li className="action-last-colum">
                    <img
                      id={`edit-biomarker-${index + 1}`}
                      onClick={() => handleEdit(item?._id)}
                      src={editIcon}
                      alt=""
                      className={`${!hasWriteAccess ? "read_access" : ""}`}
                    />
                    <img
                      id={`delete-biomarker-${index + 1}`}
                      onClick={() => handleDelete(item?._id)}
                      src={deleteIcon}
                      alt=""
                      className={`${!hasWriteAccess ? "read_access" : ""}`}
                    />
                  </li>
                </ul>
              ))}
            </div>
          </TableWrapper>
        </div>
      )}
    </>
  );
};

export default Biomarkers;
